import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Card, CardContent, Dialog, DialogContent, DialogContentText, Grid, Theme, Typography } from '@mui/material';
import Joi from 'joi';
import { useCallback, useEffect, useState } from 'react';
import StyledCardTitleBar from '../../../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../../../components/common/StyledDialogActions';
import QuickSelectInventory from '../../../../components/inventory/QuickSelectInventory';
import MessageUsersFormAddMedias from '../../../../components/services/messageUsers/sendMessage/MessageUsersFormAddMedias';
import MessageUsersFormEditor from '../../../../components/services/messageUsers/sendMessage/MessageUsersFormEditor';
import MessageUsersPriceLock from '../../../../components/services/messageUsers/sendMessage/MessageUsersPriceLock';
import MessageUsersTagCreators from '../../../../components/services/messageUsers/sendMessage/MessageUsersTagCreators';
import { InventoryVersion } from '../../../../hooks/useInventory';
import { RestructuredResponseItemSchema } from '../../../../hooks/useSextforceAutoCampaignTipReply';
import useSextforceBigBrotherRestrictedWords from '../../../../hooks/useSextforceBigBrotherRestrictedWords';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import useSubscriberMedia from '../../../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditonal from '../../../../hooks/useSubscriberVaultMediasAdditional';
import {
    createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails,
    OnlyFansSubscriberMessageUsersAutoMessageDetails,
    OnlyFansSubscriberMessageUsersAutoMessageDetailsSchema,
} from '../../../../types/messageUsersAutoSendWelcomeMessage';
import { isSextforceActive, stripHtmlAndConvertParagraphs } from '../../../../utils/common';
import { getRestrictedWordsInMessage } from '../../../../utils/messageUsers';

type Props = {
    subscriber: OnlyFansSubscriber;
    open: boolean;
    onClose: () => void;
    /** Index in `formData.replies` being edited; must be passed from parent so save is not affected by stale closure state. */
    replyIndex: number;
    /** Normalized message details (use `normalizeFreeloadersReplyToMessageDetails` when opening). */
    initialMessageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails;
    theme: Theme;
    onSave: (details: OnlyFansSubscriberMessageUsersAutoMessageDetails, replyIndex: number) => void;
    title?: string;
};

const HostessAutoReplyFreeloadersMessageDetailsDialog = (props: Props) => {
    const { subscriber, open, onClose, replyIndex, initialMessageDetails, theme, onSave, title = 'Edit reply message' } = props;

    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();

    const [messageDetails, setMessageDetails] = useState<OnlyFansSubscriberMessageUsersAutoMessageDetails>(initialMessageDetails);

    const errors: Joi.ValidationErrorItem[] | null =
        OnlyFansSubscriberMessageUsersAutoMessageDetailsSchema.validate(messageDetails).error?.details || null;

    const [includeMedia, setIncludeMedia] = useState<boolean>(initialMessageDetails.mediaFiles.length > 0);
    const [vaultAlbum, setVaultAlbum] = useState<number>(-1);
    const [vaultAlbumName, setVaultAlbumName] = useState<string | undefined>(undefined);
    const [medias, setMedias] = useState<OnlyFansVaultMedias.RestructuredResponseItem[]>(initialMessageDetails.mediaFiles || []);
    const [mediasPreviews, setMediasPreviews] = useState<number[]>(initialMessageDetails.mediaFilesPreviews || []);
    const [thumbnails, setThumbnails] = useState<{ thumbId: number; blob: Blob | null }[]>([]);
    const [tagCreators, setTagCreators] = useState<boolean>(
        initialMessageDetails.taggedCreators.length > 0 ||
            (initialMessageDetails.taggedReleaseForms && initialMessageDetails.taggedReleaseForms.length > 0)
            ? true
            : false,
    );
    const [taggedCreators, setTaggedCreators] = useState<{ id: number; name: string }[]>(initialMessageDetails.taggedCreators || []);
    const [taggedReleaseForms, setTaggedReleaseForms] = useState<{ id: number; name: string }[]>(
        initialMessageDetails.taggedReleaseForms || [],
    );
    const [lockMessage, setLockMessage] = useState<boolean>(initialMessageDetails.lockMessagePrice !== null);
    const [lockedText, setLockedText] = useState<boolean>(initialMessageDetails.lockedText || false);
    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const [saveTemplate, setSaveTemplate] = useState<boolean>(false);
    const [saveTemplateName, setSaveTemplateName] = useState<string>('');
    const [selectedInventory, setSelectedInventory] = useState<InventoryVersion>();

    const { getThumbnail: getSubscriberThumbnail, getAudio: getSubscriberAudio } = useSubscriberMedia();
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditonal();

    const taggedCreatorsCount = messageDetails.taggedCreators.length;
    const taggedReleaseFormsCount = messageDetails.taggedReleaseForms ? messageDetails.taggedReleaseForms.length : 0;
    const totalTagged = taggedCreatorsCount + taggedReleaseFormsCount;

    useEffect(() => {
        const switchInventory = async () => {
            if (selectedInventory) {
                const next: OnlyFansSubscriberMessageUsersAutoMessageDetails = {
                    ...createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails(),
                    ...messageDetails,
                    lockMessagePrice: 0,
                    taggedCreators: [],
                    mediaFiles: [],
                    mediaFilesPreviews: [],
                };

                setIncludeMedia(false);
                setMedias([]);
                setMediasPreviews([]);
                setThumbnails([]);
                setTagCreators(false);
                setTaggedCreators([]);
                setTaggedReleaseForms([]);
                setLockMessage(false);
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);

                next.message = selectedInventory.text;

                if (Number(selectedInventory.price) > 0) {
                    setLockMessage(true);
                    next.lockMessagePrice = Number(selectedInventory.price);
                }

                if (selectedInventory.creatorTags && selectedInventory.creatorTags.length > 0) {
                    setTagCreators(true);
                    const mapped = selectedInventory.creatorTags.map(item => ({
                        id: item.creatorOnlyfansId,
                        name: item.creatorName,
                    }));
                    setTaggedCreators(mapped);
                    next.taggedCreators = mapped;
                }

                if ((selectedInventory.unlockedMedias?.length ?? 0) > 0 || (selectedInventory.lockedMedias?.length ?? 0) > 0) {
                    setIncludeMedia(true);
                    const grabMedias = await getManyVaultMediaFromDatabase([
                        ...(selectedInventory.lockedMedias?.map(item => item.mediaId) ?? []),
                        ...(selectedInventory.unlockedMedias?.map(item => item.mediaId) ?? []),
                    ]);
                    next.mediaFiles = grabMedias;
                    setMedias(grabMedias);
                    if ((selectedInventory.unlockedMedias?.length ?? 0) > 0) {
                        const previews = selectedInventory.unlockedMedias?.map(item => item.mediaId) ?? [];
                        setMediasPreviews(previews);
                        next.mediaFilesPreviews = previews;
                    }
                }

                setMessageDetails(next);
            }
        };
        switchInventory();
        // Intentionally only when inventory row changes (matches auto-campaign tip reply dialog).
    }, [selectedInventory]);

    useEffect(() => {
        if (open) {
            setSelectedInventory(undefined);
            setMessageDetails(initialMessageDetails);
            setIncludeMedia(initialMessageDetails.mediaFiles.length > 0);
            setVaultAlbum(-1);
            setVaultAlbumName(undefined);
            setMedias(initialMessageDetails.mediaFiles || []);
            setMediasPreviews(initialMessageDetails.mediaFilesPreviews || []);
            setThumbnails([]);
            setTagCreators(
                initialMessageDetails.taggedCreators.length > 0 ||
                    (initialMessageDetails.taggedReleaseForms && initialMessageDetails.taggedReleaseForms.length > 0)
                    ? true
                    : false,
            );
            setTaggedCreators(initialMessageDetails.taggedCreators || []);
            setTaggedReleaseForms(initialMessageDetails.taggedReleaseForms || []);
            setLockMessage(initialMessageDetails.lockMessagePrice !== null);
            setLockedText(initialMessageDetails.lockedText || false);
            setMessageHasRestrictedWords(false);
            setMessageFoundRestrictedWords([]);
            setSaveTemplate(false);
            setSaveTemplateName('');
        }
    }, [open, initialMessageDetails]);

    const checkForRestrictedWords = useCallback(
        (text: string) => {
            if (text && !isLoadingRestrictedWords && restrictedWords && subscriber) {
                const restrictedWordsFound = getRestrictedWordsInMessage(subscriber, text, restrictedWords);
                setMessageHasRestrictedWords(restrictedWordsFound.length > 0);
                setMessageFoundRestrictedWords(restrictedWordsFound);
            } else {
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);
            }
        },
        [isLoadingRestrictedWords, restrictedWords, subscriber],
    );

    useEffect(() => {
        setMessageDetails(prev => ({ ...prev, mediaFiles: medias }));
    }, [medias]);

    useEffect(() => {
        setMessageDetails(prev => ({ ...prev, mediaFilesPreviews: mediasPreviews }));
    }, [mediasPreviews]);

    useEffect(() => {
        setMessageDetails(prev => ({ ...prev, taggedCreators }));
    }, [taggedCreators]);

    useEffect(() => {
        setMessageDetails(prev => ({ ...prev, taggedReleaseForms }));
    }, [taggedReleaseForms]);

    const getThumbnail = (thumbId: number, thumbUrl: string) => {
        getSubscriberThumbnail(subscriber, thumbId, thumbUrl)?.then(blob => {
            setThumbnails(prev => [...prev, { thumbId, blob: blob || null }]);
        });
    };

    const getAudio = (url: string) => getSubscriberAudio(subscriber, url);

    const handleSave = () => {
        if (replyIndex < 0) {
            return;
        }
        onSave(messageDetails, replyIndex);
        onClose();
    };

    const handleClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') {
            return;
        }
        onClose();
    };

    const mediaJoiError = Joi.array().items(RestructuredResponseItemSchema).validate(medias).error;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
            <StyledCardTitleBar title={title} />
            <DialogContent>
                <DialogContentText>
                    <Typography variant="body1" gutterBottom>
                        Configure the full message for this step in the freeloader reply sequence (text, vault media, PPV, tags, and
                        username placeholder).
                    </Typography>
                </DialogContentText>
                <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                    <Grid item xs={12}>
                        <Card variant="elevation">
                            <CardContent>
                                <QuickSelectInventory setSelectedInventoryVersion={setSelectedInventory} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <MessageUsersFormAddMedias
                            subscriber={subscriber}
                            serviceName="autoSendWelcomeMessage"
                            includeMedia={includeMedia}
                            setIncludeMedia={setIncludeMedia}
                            vaultAlbum={vaultAlbum}
                            setVaultAlbum={setVaultAlbum}
                            vaultAlbumName={vaultAlbumName}
                            setVaultAlbumName={setVaultAlbumName}
                            medias={medias}
                            setMedias={setMedias}
                            mediasPreviews={mediasPreviews}
                            setMediasPreviews={setMediasPreviews}
                            thumbnails={thumbnails}
                            getThumbnail={getThumbnail}
                            getAudio={getAudio}
                            tagCreators={tagCreators}
                            setTagCreators={setTagCreators}
                            taggedCreators={taggedCreators}
                            setTaggedCreators={setTaggedCreators}
                            taggedReleaseForms={taggedReleaseForms}
                            setTaggedReleaseForms={setTaggedReleaseForms}
                            session={null}
                            albumsSelectorSize="small"
                            disabled={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        {includeMedia && (
                            <MessageUsersTagCreators
                                subscriber={subscriber}
                                tagCreators={tagCreators}
                                setTagCreators={setTagCreators}
                                taggedCreators={taggedCreators}
                                setTaggedCreators={setTaggedCreators}
                                taggedReleaseForms={taggedReleaseForms}
                                setTaggedReleaseForms={setTaggedReleaseForms}
                                session={null}
                                disabled={false}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <MessageUsersPriceLock
                            subscriber={subscriber}
                            lockMessage={lockMessage}
                            setLockMessage={checked => {
                                setLockMessage(checked);
                                setMessageDetails(prev => ({
                                    ...prev,
                                    lockMessagePrice: checked ? prev.lockMessagePrice || 0 : null,
                                }));
                            }}
                            lockMessagePrice={messageDetails.lockMessagePrice || 0}
                            setLockMessagePrice={lockMessagePrice => {
                                setMessageDetails(prev => ({
                                    ...prev,
                                    lockMessagePrice,
                                }));
                            }}
                            lockedText={lockedText}
                            setLockedText={lockedTextState => {
                                setMessageDetails(prev => ({
                                    ...prev,
                                    lockedText: lockedTextState,
                                }));
                            }}
                            medias={medias}
                            thumbnails={thumbnails}
                            mediasPreviews={mediasPreviews}
                            setMediasPreviews={setMediasPreviews}
                            session={null}
                            disabled={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <MessageUsersFormEditor
                            subscriber={subscriber}
                            message={messageDetails.message || ''}
                            setMessage={message => {
                                setMessageDetails(prev => ({
                                    ...prev,
                                    message,
                                }));
                            }}
                            messageHasRestrictedWords={messageHasRestrictedWords}
                            messageFoundRestrictedWords={messageFoundRestrictedWords}
                            checkForRestrictedWords={checkForRestrictedWords}
                            usernameReplacement={messageDetails.replaceUsername || ''}
                            setUsernameReplacement={(replaceUsername: string) => {
                                setMessageDetails(prev => ({
                                    ...prev,
                                    replaceUsername,
                                }));
                            }}
                            taggedCreators={taggedCreators}
                            taggedReleaseForms={taggedReleaseForms}
                            lockMessage={lockMessage}
                            lockMessagePrice={messageDetails.lockMessagePrice || 0}
                            lockedText={lockedText}
                            saveTemplate={saveTemplate}
                            setSaveTemplate={setSaveTemplate}
                            saveTemplateName={saveTemplateName}
                            setSaveTemplateName={setSaveTemplateName}
                            session={null}
                            borderless={false}
                            theme={theme}
                            disabled={false}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <StyledDialogActions>
                <Button color="error" size="medium" onClick={() => handleClose({}, 'escapeKeyDown')}>
                    Close
                </Button>
                <LoadingButton
                    color="secondary"
                    variant="contained"
                    size="medium"
                    loading={false}
                    disabled={
                        replyIndex < 0 ||
                        !!errors ||
                        !!mediaJoiError ||
                        (messageHasRestrictedWords && isSextforceActive(subscriber) && subscriber.sextforce?.bigBrother?.active) ||
                        (includeMedia && !medias.length) ||
                        (tagCreators && totalTagged === 0) ||
                        (lockMessage && (!messageDetails.lockMessagePrice || messageDetails.lockMessagePrice < 3)) ||
                        stripHtmlAndConvertParagraphs(messageDetails.message).trim().length === 0
                    }
                    onClick={handleSave}
                >
                    <span>Save reply</span>
                </LoadingButton>
            </StyledDialogActions>
        </Dialog>
    );
};

export default HostessAutoReplyFreeloadersMessageDetailsDialog;
